import React, { useEffect, useState, useRef } from 'react';
import { Box, Typography, Divider } from '@mui/material';
import axios from 'axios';
import html2canvas from 'html2canvas';
import ChartGrid from './ChartGrid';
import { resolveReportLogoSrc } from './ReportPdfHeader';
import { getDashboardQueryDataKey, buildParamsForChart } from '../utils/dashboardData';
import { dashboardForDisplay } from '../utils/chartLibrary';
import { waitForReportChartVisuals, REPORT_PDF_CAPTURE_WIDTH } from '../utils/reportChartReady';
import { buildReportPdf } from '../utils/buildReportPdf';
import vsaasTheme from '../theme';

/** Mismo default que server/reportQueryLoader.js y ReportPreview: la clave debe coincidir. */
const DEFAULT_REPORT_TIMEZONE = 'America/Mexico_City';

const getPreviewDataKey = (dashboardId, queryId, params, timezone) =>
  `${dashboardId}|${getDashboardQueryDataKey(queryId, params, timezone || DEFAULT_REPORT_TIMEZONE)}`;

/** Mismo ancho de captura que Descargar PDF / html2canvas. */
const PREVIEW_PDF_CONTENT_WIDTH = REPORT_PDF_CAPTURE_WIDTH;

function shouldGeneratePdf() {
  if (typeof window === 'undefined') return true;
  const params = new URLSearchParams(window.location.search);
  return params.get('generatePdf') !== '0';
}

/**
 * Vista headless para Puppeteer: renderiza dashboards y genera el mismo PDF que "Descargar PDF".
 * Los datos los inyecta el generador del backend en `window.__getPrintReportPayload`;
 * fuera de ese Chrome la vista no tiene datos que mostrar.
 */
function PrintReportPage({ reportId }) {
  const [report, setReport] = useState(null);
  const [dashboards, setDashboards] = useState([]);
  const [queryDataByQueryId, setQueryDataByQueryId] = useState({});
  const [reportLogo, setReportLogo] = useState('');
  const [reportLogoData, setReportLogoData] = useState('');
  const [error, setError] = useState(null);
  const dashboardRefs = useRef({});

  const base = axios.defaults.baseURL || '';
  const generatePdf = shouldGeneratePdf();

  useEffect(() => {
    document.documentElement.style.overflow = 'visible';
    document.body.style.overflow = 'visible';
    document.body.style.margin = '0';
    document.body.style.background = vsaasTheme.background.default;
    return () => {
      document.documentElement.style.overflow = '';
      document.body.style.overflow = '';
      document.body.style.margin = '';
      document.body.style.background = '';
    };
  }, []);

  useEffect(() => {
    const loadPayload = typeof window !== 'undefined' ? window.__getPrintReportPayload : null;
    if (typeof loadPayload !== 'function') {
      setError('La vista de impresión solo está disponible para el generador de reportes');
      return;
    }
    Promise.resolve(loadPayload())
      .then((payload) => {
        if (payload?.report && String(payload.report.id) === String(reportId)) {
          setReport(payload.report);
          setReportLogo(payload.reportLogo || '');
          setReportLogoData(payload.reportLogoData || '');
          const dashs = payload.dashboards || [];
          setDashboards(dashs);
          if (payload.queryDataByQueryId && typeof payload.queryDataByQueryId === 'object') {
            setQueryDataByQueryId(payload.queryDataByQueryId);
            return;
          }
          const byKey = {};
          const opts = (d) => ({
            globalFilterValues: d.globalFilterValues || {},
            globalFilterParamAliases: d.globalFilterParamAliases || {},
            globalFilterParamNames: d.globalFilterParamNames || [],
            getDynamicFilterValue: (ch, paramName) => {
              const list = Array.isArray(ch?.advancedConfig?.dynamicFilters)
                ? ch.advancedConfig.dynamicFilters
                : (ch?.advancedConfig?.dynamicFilter ? [ch.advancedConfig.dynamicFilter] : []);
              const found = list.find((f) => String(f?.paramName || '').trim() === String(paramName || '').trim());
              return found?.selectedValue;
            },
          });
          dashs.forEach((d) => {
            const display = dashboardForDisplay(d);
            (display.charts || []).forEach((ch) => {
              if (ch.queryId == null) return;
              const params = buildParamsForChart(ch, opts(display));
              const key = getPreviewDataKey(display.id, ch.queryId, params, d.timezone);
              byKey[key] = display.queryData || d.queryData || { columns: [], data: [] };
            });
          });
          setQueryDataByQueryId(byKey);
        } else {
          setError('No se pudo cargar el reporte');
        }
      })
      .catch((err) => {
        setError(err?.message || 'Error al cargar el reporte');
      });
  }, [reportId]);

  useEffect(() => {
    if (!report || error) return;
    const t = setTimeout(() => window.dispatchEvent(new Event('resize')), 300);
    return () => clearTimeout(t);
  }, [report, error, dashboards, queryDataByQueryId]);

  useEffect(() => {
    if (!report || error || !generatePdf) return;
    let cancelled = false;

    const run = async () => {
      const root = document.getElementById('print-report-root');
      if (root) {
        await waitForReportChartVisuals(root, { timeoutMs: 45000 });
      }
      window.dispatchEvent(new Event('resize'));
      await new Promise((r) => setTimeout(r, 500));

      if (cancelled) return;

      const dashboardIds = Array.isArray(report.dashboards) ? report.dashboards : [];
      const resolved = dashboardIds
        .map((id) => dashboards.find((d) => d && (Number(d.id) === Number(id) || d.id === id)))
        .filter(Boolean);

      const dashboardCaptures = resolved.map((d) => ({
        name: d.name,
        getElement: () => dashboardRefs.current[d.id],
      }));

      // report.schedule.pdfMode === 'perDashboard': un PDF por dashboard del reporte,
      // todos adjuntos al mismo correo (o descargables uno a uno), en vez del PDF combinado.
      const perDashboardMode = report.schedule?.pdfMode === 'perDashboard' && resolved.length > 0;

      try {
        const reportLogoPath = resolveReportLogoSrc({ reportLogoData, reportLogo, baseUrl: base });

        if (perDashboardMode) {
          const pdfs = [];
          for (const d of resolved) {
            const perDashboardReport = { ...report, name: d.name ? `${report.name} - ${d.name}` : report.name };
            const { fileName, base64, bytes, compressionAttempt } = await buildReportPdf({
              report: perDashboardReport,
              reportLogoPath,
              dashboardCaptures: [{ name: d.name, getElement: () => dashboardRefs.current[d.id] }],
              html2canvas,
              compressForEmail: true,
            });
            console.info(
              '[PrintReportPage] PDF correo:',
              d.name,
              `${Math.round(bytes / 1024)} KB`,
              compressionAttempt
                ? `escala ${compressionAttempt.scale}, ${compressionAttempt.useJpeg ? `JPEG ${compressionAttempt.jpegQuality}` : 'PNG'}`
                : ''
            );
            pdfs.push({ fileName, base64 });
          }
          window.__reportPdfResult = { pdfs };
        } else {
          const { fileName, base64, bytes, compressionAttempt } = await buildReportPdf({
            report,
            reportLogoPath,
            dashboardCaptures,
            html2canvas,
            compressForEmail: true,
          });

          console.info(
            '[PrintReportPage] PDF correo:',
            `${Math.round(bytes / 1024)} KB`,
            compressionAttempt
              ? `escala ${compressionAttempt.scale}, ${compressionAttempt.useJpeg ? `JPEG ${compressionAttempt.jpegQuality}` : 'PNG'}`
              : ''
          );

          window.__reportPdfResult = { fileName, base64 };
        }

        const el = document.getElementById('print-report-root');
        if (el) {
          el.setAttribute('data-pdf-ready', 'true');
          el.setAttribute('data-report-ready', 'true');
        }
      } catch (err) {
        console.error('PrintReportPage: error al generar PDF', err);
        const el = document.getElementById('print-report-root');
        if (el) {
          el.setAttribute('data-pdf-error', err?.message || 'Error al generar PDF');
          el.setAttribute('data-report-ready', 'true');
        }
        window.__reportPdfResult = { error: err?.message || 'Error al generar PDF' };
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [report, error, dashboards, queryDataByQueryId, generatePdf, reportLogo, reportLogoData, base]);

  if (error) {
    return (
      <div id="print-report-root" data-pdf-error={error} style={{ padding: 20 }}>
        <Typography color="error">{error}</Typography>
      </div>
    );
  }
  if (!report) {
    return <div id="print-report-root" style={{ padding: 20 }}><Typography>Cargando reporte…</Typography></div>;
  }

  const dashboardIds = Array.isArray(report.dashboards) ? report.dashboards : [];
  const resolved = dashboardIds
    .map((id) => dashboards.find((d) => d && (Number(d.id) === Number(id) || d.id === id)))
    .filter(Boolean);

  return (
    <div
      id="print-report-root"
      style={{
        padding: 16,
        backgroundColor: vsaasTheme.background.default,
        width: PREVIEW_PDF_CONTENT_WIDTH,
        maxWidth: '100%',
        boxSizing: 'border-box',
        overflow: 'visible',
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4, py: 2 }}>
        {resolved.map((fullDashboard, index) => {
          const display = dashboardForDisplay(fullDashboard);
          return (
            <Box
              key={fullDashboard.id || index}
              sx={{
                backgroundColor: '#ffffff',
                p: 2,
                borderRadius: 1,
              }}
            >
              <Typography variant="h5" gutterBottom>
                {index + 1}. {display.name || 'Dashboard sin nombre'}
              </Typography>
              <Divider sx={{ my: 2 }} />
              <Box
                ref={(el) => {
                  if (el && fullDashboard.id != null) {
                    dashboardRefs.current[fullDashboard.id] = el;
                  }
                }}
                data-report-dashboard-capture="true"
                sx={{ minHeight: '400px', width: '100%', overflow: 'visible' }}
              >
                <ChartGrid
                  printCapture
                  dashboardTimezone={fullDashboard.timezone}
                  queryData={display.queryData}
                  queryDataByQueryId={queryDataByQueryId}
                  getQueryDataKey={(qid, p) => getPreviewDataKey(display.id, qid, p, fullDashboard.timezone)}
                  globalFilterParamNames={display.globalFilterParamNames || []}
                  globalFilterValues={display.globalFilterValues || {}}
                  globalFilterParamAliases={display.globalFilterParamAliases || {}}
                  charts={display.charts || []}
                  onChartsChange={() => {}}
                  query={display.query || ''}
                  onQueryExecute={() => {}}
                  selectedDatabase={display.database || ''}
                  savedQueries={[]}
                  selectedQueryId={null}
                  onQuerySelect={() => {}}
                  layout={display.layout || []}
                  onLayoutChange={() => {}}
                  dashboardName={display.name || ''}
                />
              </Box>
            </Box>
          );
        })}
      </Box>
    </div>
  );
}

export default PrintReportPage;
